/**
 * Priority-specific extraction functions for extractCityFromPost.
 * Each function handles one priority level of city extraction.
 */

import { CityFormData } from '@/types/city';
import City from '@/models/city';
import { debug } from '@/utils/logger';
import type {
  UserDefinedDestination,
  PostLocation,
  AddressComponent,
} from './cityExtractionTypes';
import {
  extractCityNameFromDestination,
  extractGeopositionFromDestination,
  constructCompleteIdentifyingName,
  determineDisplayAddress,
  extractCityFromCityModel,
  extractCityFromPlainObject,
} from './cityExtractionHelpers';

/**
 * Priority 2 (for Itineraries): Extract city from itinerary_data.user_defined_destinations[0]
 * This is checked FIRST for Itineraries because post.cities[0] may have incomplete data.
 * 
 * @param destination - First user-defined destination from itinerary_data
 * @returns CityFormData or null
 */
export function extractCityFromItineraryDestination(
  destination: UserDefinedDestination
): CityFormData | null {
  if (!destination || typeof destination !== 'object') {
    return null;
  }

  debug('[extractCityFromItineraryDestination] Extracting from itinerary destination');

  const fullName = typeof destination.name === 'string' ? destination.name : '';
  const cityName = extractCityNameFromDestination(destination);
  const a3IntlAddress = typeof destination.a3_intl_address === 'string' ? destination.a3_intl_address : '';

  debug('[extractCityFromItineraryDestination] Extracting from user_defined_destinations', {
    fullName,
    cityName,
    a3IntlAddress,
    hasA3IntlAddress: !!a3IntlAddress,
    isShortFormat: a3IntlAddress ? /,\s*[A-Z]{2}\s*,/.test(a3IntlAddress) : false,
    destKeys: Object.keys(destination),
  });

  const completeName = constructCompleteIdentifyingName(cityName, fullName);
  const displayAddress = determineDisplayAddress(a3IntlAddress, completeName);

  debug('[extractCityFromItineraryDestination] Final display address for Itinerary', {
    displayAddress,
    isShortFormat: /,\s*[A-Z]{2}\s*,/.test(displayAddress),
    source: a3IntlAddress && /,\s*[A-Z]{2}\s*,/.test(a3IntlAddress) ? 'a3_intl_address' : 'completeName',
  });

  const geoPosition = extractGeopositionFromDestination(destination);

  return {
    _id: typeof destination.location_id === 'string' ? destination.location_id :
         typeof destination.id === 'string' ? destination.id : undefined,
    city: cityName,
    name: cityName,
    city_identifying_name: completeName,
    a3_intl_address: displayAddress,
    long_name: completeName,
    google_place_id: typeof destination.google_place_id === 'string' ? destination.google_place_id : undefined,
    ...(geoPosition && { geo_position: geoPosition }),
  };
}

/**
 * Priority 1: Extract city from post.cities[0] (Lists from mobile app)
 * Mobile app ALWAYS populates this for Lists.
 * 
 * @param cities - Array of cities from post.cities
 * @returns CityFormData or null
 */
export function extractCityFromPostCities(
  cities: (City | Record<string, unknown>)[]
): CityFormData | null {
  if (!Array.isArray(cities) || cities.length === 0) {
    return null;
  }

  const city = cities[0];
  
  if (city instanceof City) {
    debug('[extractCityFromPostCities] Found city in post.cities[0] (City model)', {
      _id: city._id,
      short_name: city.short_name,
      long_name: city.long_name,
      identifying_name: city.identifying_name,
      hasGooglePlacesData: !!city.google_places_data,
      googlePlacesDataKeys: city.google_places_data ? Object.keys(city.google_places_data as Record<string, unknown>) : [],
    });
    
    return extractCityFromCityModel(city);
  } else {
    // City might be a plain object, not a City instance
    debug('[extractCityFromPostCities] post.cities[0] is not a City instance, treating as plain object', {
      cityType: typeof city,
      cityKeys: city && typeof city === 'object' ? Object.keys(city) : [],
    });
    
    if (city && typeof city === 'object') {
      return extractCityFromPlainObject(city as Record<string, unknown>);
    }
  }

  return null;
}

/**
 * Priority 3: Extract city from top-level user_defined_destinations[0] (web app Lists - legacy)
 * Web frontend may have created Lists with this structure.
 * 
 * @param destination - First user-defined destination from top level
 * @param cityIdentifyingNames - Array of city identifying names from post
 * @returns CityFormData or null
 */
export function extractCityFromTopLevelDestination(
  destination: UserDefinedDestination,
  cityIdentifyingNames?: string[]
): CityFormData | null {
  if (!destination || typeof destination !== 'object') {
    return null;
  }

  debug('[extractCityFromTopLevelDestination] Found city in top-level user_defined_destinations[0]');

  const cityName = typeof destination.name === 'string' ? destination.name : '';
  const identifyingName = (cityIdentifyingNames && Array.isArray(cityIdentifyingNames) && cityIdentifyingNames.length > 0)
    ? cityIdentifyingNames[0]
    : '';

  // Ensure identifying_name is complete
  const completeIdentifyingName = identifyingName && cityName
    ? (identifyingName.toLowerCase().startsWith(cityName.toLowerCase())
        ? identifyingName
        : `${cityName}, ${identifyingName}`)
    : identifyingName || cityName;

  return {
    _id: typeof destination.location_id === 'string' ? destination.location_id : undefined,
    city: cityName,
    city_identifying_name: completeIdentifyingName,
    a3_intl_address: completeIdentifyingName,
    google_place_id: typeof destination.google_place_id === 'string' ? destination.google_place_id : undefined,
    ...(typeof destination.lat === 'number' && typeof destination.lng === 'number' && {
      geo_position: {
        lat: destination.lat,
        long: destination.lng,
      },
    }),
  };
}

/**
 * Priority 4: Extract city from city_identifying_names[0] (fallback - backend may populate)
 * Backend extracts this from other sources, but may be incomplete.
 * 
 * @param identifyingName - First city identifying name from post
 * @returns CityFormData or null
 */
export function extractCityFromIdentifyingName(
  identifyingName: string
): CityFormData | null {
  if (!identifyingName || typeof identifyingName !== 'string' || !identifyingName.trim()) {
    return null;
  }

  debug('[extractCityFromIdentifyingName] Found city_identifying_name, creating minimal city object');
  
  // Extract city name from identifying_name (usually "City, State, Country")
  const cityName = identifyingName.split(',')[0]?.trim() || identifyingName;
  
  return {
    city: cityName,
    city_identifying_name: identifyingName,
    a3_intl_address: identifyingName,
  };
}

/**
 * Priority 5: Extract city from postLocations[0].location (fallback for Lists)
 * This is a fallback when backend city extraction failed but postLocations have city data.
 * Only used for Lists (Itineraries should have found city in priority 2).
 * 
 * @param postLocations - Array of post locations
 * @returns CityFormData or null
 */
export function extractCityFromPostLocations(
  postLocations: PostLocation[]
): CityFormData | null {
  if (!Array.isArray(postLocations) || postLocations.length === 0) {
    return null;
  }

  const firstPostLocation = postLocations[0];
  const location = firstPostLocation?.location;
  
  if (!location || typeof location !== 'object') {
    return null;
  }

  // Try to extract from google_places_data.addressComponents (same format backend expects)
  const googlePlacesData = location.google_places_data;
  if (googlePlacesData && typeof googlePlacesData === 'object') {
    const addressComponents = googlePlacesData.addressComponents;
    if (Array.isArray(addressComponents) && addressComponents.length > 0) {
      // Extract city, state, country from addressComponents (same logic backend uses)
      const locality = addressComponents.find((comp: AddressComponent) => {
        const types = comp.types;
        return Array.isArray(types) && types.includes('locality');
      });
      const adminArea = addressComponents.find((comp: AddressComponent) => {
        const types = comp.types;
        return Array.isArray(types) && types.includes('administrative_area_level_1');
      });
      const country = addressComponents.find((comp: AddressComponent) => {
        const types = comp.types;
        return Array.isArray(types) && types.includes('country');
      });
      
      const cityName = typeof locality?.longText === 'string' ? locality.longText : 
                      typeof locality?.long_name === 'string' ? locality.long_name : '';
      const stateName = typeof adminArea?.longText === 'string' ? adminArea.longText :
                       typeof adminArea?.long_name === 'string' ? adminArea.long_name : '';
      const countryName = typeof country?.longText === 'string' ? country.longText :
                         typeof country?.long_name === 'string' ? country.long_name : '';
      
      if (cityName) {
        const parts = [cityName];
        if (stateName) parts.push(stateName);
        if (countryName) parts.push(countryName);
        const identifyingName = parts.join(', ');
        
        debug('[extractCityFromPostLocations] Extracted city from addressComponents', {
          cityName,
          identifyingName,
          hasState: !!stateName,
          hasCountry: !!countryName,
        });
        
        return {
          city: cityName,
          city_identifying_name: identifyingName,
          a3_intl_address: identifyingName,
          google_places_data: googlePlacesData as CityFormData['google_places_data'],
        };
      }
    }
  }
  
  // Fallback: Try a3_intl_address or address field
  const a3IntlAddress = typeof location.a3_intl_address === 'string' ? location.a3_intl_address : undefined;
  const address = typeof location.address === 'string' ? location.address : undefined;
  const identifyingName = a3IntlAddress || address;
  
  if (identifyingName && identifyingName.trim()) {
    const cityName = identifyingName.split(',')[0]?.trim() || identifyingName;
    debug('[extractCityFromPostLocations] Extracted city from address field', {
      cityName,
      identifyingName,
      source: a3IntlAddress ? 'a3_intl_address' : 'address',
    });
    
    return {
      city: cityName,
      city_identifying_name: identifyingName,
      a3_intl_address: identifyingName,
    };
  }

  return null;
}
